import m from '@bitstillery/mithril'
import {MithrilComponent, state} from '@bitstillery/mithril'
import type {State} from '@bitstillery/mithril'
import {Button, FieldCode, FieldText} from '@bitstillery/common/components'
import {and, email, required, validation, type ValidationFailure} from '@bitstillery/common/lib/validation'
import {$s, $t} from '@bitstillery/common/app'
import {present} from '@bitstillery/common/lib/utils'

import QR from './qr'

interface AuthLoginData {
    form: {
        email_address: string
        email_support: string
        otp_code: string
        password: string
    }
    otp_fail: string | null
    otp_secret: string | null
    pre_auth_token: string | null
    stepper: {
        options: {slug: number; title: string}[]
        selection: number
    }
    submitting: boolean
    support_mode: boolean
}

export abstract class AuthLogin<Attrs = unknown> extends MithrilComponent<Attrs> {
    /** When set (e.g. portal auth dialog), forgot-password uses this instead of routing to `/user/forgot-password` */
    protected on_forgot_password_click: (() => void) | undefined = undefined

    data: State<AuthLoginData> = state(
        {
            form: {
                email_address: '',
                email_support: '',
                otp_code: '',
                password: '',
            },
            otp_fail: null,
            // One-time token confirmation
            otp_secret: null,
            pre_auth_token: null,
            submitting: false,
            stepper: {
                options: [
                    {slug: 1, title: $t('landing.login.step0_slug', {vendor: process.env.MSI_TITLE})},
                    {slug: 2, title: $t('landing.login.step1_slug')},
                    {slug: 3, title: $t('landing.login.step2_slug')},
                ],
                selection: 0,
            },
            support_mode: Boolean(m.route.param('support')),
        },
        'auth-login-data',
    )

    $v = {
        email_address: validation(this.data.form.$email_address, and([required(), email()])),
        otp_code: validation(this.data.form.$otp_code, {
            label: '*',
            message: '',
            validate: (value: string): boolean | ValidationFailure => {
                if (value.length < 6) {
                    return {message: $t('status.error.otp.length')}
                } else if (this.data.otp_fail) {
                    return {message: $t('status.error.otp.invalid')}
                }
                return false
            },
        }),
        password: validation(this.data.form.$password, required()),
    }

    is_invalid() {
        if (!this.validationGroup[this.data.stepper.selection]) {
            return false
        }
        const validation_fields = present(this.validationGroup[this.data.stepper.selection], 'step validation fields')
        const invalid = validation_fields.map((i) => this.$v[i]).filter((i) => i._invalid)
        return invalid.length > 0
    }

    validationGroup: Partial<Record<number, (keyof AuthLogin['$v'])[]>> = {
        0: ['email_address', 'password'],
        1: ['otp_code'],
    }

    /**
     * Run a submit step with the control in a spinner state, and drop double submits.
     *
     * Cleared only when the step lands back in this same form — a failure, or the step to OTP. A
     * successful login navigates away, and mithril keeps this view on screen while the router resolves
     * the page it is going to (the portal's home page readies itself first), so the spinner is the only
     * thing saying the click landed for as long as that takes.
     */
    protected async submit(step: () => void | Promise<void>): Promise<void> {
        if (this.data.submitting) {
            return
        }
        const submitted_from = $s.env.uri
        this.data.submitting = true
        try {
            await step()
        } finally {
            if ($s.env.uri === submitted_from) {
                this.data.submitting = false
            }
        }
    }

    redirect_to = m.route.param('redirect')

    abstract login(): void
    abstract verify_otp(): void

    view() {
        return (
            <div className='c-auth-login landing'>
                <form
                    className='form stepper-form'
                    novalidate={true}
                    onkeypress={(e: KeyboardEvent) => {
                        if (e.key === 'Enter' && !this.is_invalid()) {
                            void this.submit(() => this.login())
                        }
                    }}
                    onsubmit={(e: SubmitEvent) => e.preventDefault()}
                >
                    {this.data.stepper.selection === 0 && (
                        <div className='step step-unknown step-0'>
                            <h3>{$t('landing.login.step0_slug', {vendor: process.env.MSI_TITLE})}</h3>
                            <p className='step-info'>{$t('landing.login.step0_desc', {vendor: process.env.MSI_TITLE})}</p>
                            {process.env.MSI_PACKAGE === 'portal' && this.data.support_mode && (
                                <FieldText
                                    label={$t('profile.email_support')}
                                    model={this.data.form.$email_support}
                                    placeholder={$t('profile.email')}
                                />
                            )}

                            <FieldText
                                autocomplete='username'
                                autofocus={true}
                                help={$t('profile.email_help')}
                                label={$t('profile.email')}
                                model={this.data.form.$email_address}
                                placeholder={$t('profile.email_placeholder')}
                                validation={this.$v.email_address}
                            />

                            <FieldText
                                autocomplete='current-password'
                                help={$t('profile.password_help', {vendor: process.env.MSI_TITLE})}
                                label={$t('profile.password')}
                                model={this.data.form.$password}
                                placeholder={$t('profile.password_placeholder')}
                                type='password'
                                validation={this.$v.password}
                            />

                            <div className='forgot-pw'>
                                {this.on_forgot_password_click ? (
                                    <Button
                                        onclick={() => this.on_forgot_password_click?.()}
                                        text={$t('profile.forgot_pw')}
                                        type='info'
                                        variant='link'
                                    />
                                ) : (
                                    <Button
                                        link={'/user/forgot-password'}
                                        text={$t('profile.forgot_pw')}
                                        type='info'
                                        variant='link'
                                    />
                                )}
                            </div>

                            <Button
                                className='btn-login btn-submit'
                                disabled={this.is_invalid() || this.data.submitting}
                                icon='login'
                                loading={this.data.submitting}
                                onclick={() => {
                                    void this.submit(() => this.login())
                                }}
                                text={$t('profile.login')}
                                type='info'
                            />
                        </div>
                    )}

                    {this.data.stepper.selection === 1 && this.data.otp_secret && (
                        <div className='step step-1'>
                            <h3>{$t('landing.login.step1_slug')}</h3>
                            <p className='step-info'>{$t('landing.login.step1_desc', {vendor: process.env.MSI_TITLE})}</p>

                            <div className='otp-options'>
                                <div className='field-readonly-group fl-column'>
                                    <div className='field-readonly'>
                                        <div className='key'>Google Authenticator</div>
                                        <div className='value'>
                                            <Button
                                                icon='android'
                                                link='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
                                                size='l'
                                                type='info'
                                                variant='icon'
                                            />
                                            <Button
                                                icon='apple'
                                                link='https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8'
                                                size='l'
                                                type='info'
                                                variant='icon'
                                            />
                                        </div>
                                    </div>

                                    <div className='field-readonly'>
                                        <div className='key'>FreeOTP</div>
                                        <div className='value'>
                                            <Button
                                                icon='android'
                                                link='https://play.google.com/store/apps/details?id=org.fedorahosted.freeotp'
                                                size='l'
                                                type='info'
                                                variant='icon'
                                            />
                                            <Button
                                                icon='apple'
                                                link='https://itunes.apple.com/us/app/freeotp-authenticator/id872559395?mt=8'
                                                size='l'
                                                type='info'
                                                variant='icon'
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className='code'>
                                    <QR url={this.data.otp_secret} />
                                </div>
                            </div>

                            <FieldCode
                                characters={/^[0-9]$/}
                                help={$t('landing.login.otp_code_help')}
                                label={$t('landing.login.otp_code')}
                                model={this.data.form.$otp_code}
                                onchange={() => {
                                    void this.submit(() => this.verify_otp())
                                }}
                                placeholders={6}
                                validation={this.$v.otp_code}
                            />
                        </div>
                    )}

                    {this.data.stepper.selection === 2 && (
                        <div className='step step-2'>
                            <div className='otp-required'>
                                <h3>{$t('landing.login.step2_slug')}</h3>
                                <p className='step-info'>{$t('landing.login.step2_desc')}</p>

                                <FieldCode
                                    autofocus={true}
                                    characters={/^[0-9]$/}
                                    help={$t('landing.login.otp_code_help')}
                                    label={$t('landing.login.otp_code')}
                                    model={this.data.form.$otp_code}
                                    onchange={() => {
                                        void this.submit(() => this.verify_otp())
                                    }}
                                    placeholders={6}
                                    validation={this.$v.otp_code}
                                />
                            </div>
                        </div>
                    )}
                </form>
            </div>
        )
    }
}
